import { useMyBalance } from "api/Client/balance";
import { Spinner } from "components";
import useCurrentLocation from "Hooks/useCurrentLocation";
import useViewTransitionNavigate from "Hooks/useViewTransitionNavigate";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import {
  HiOutlineArrowRightCircle,
  HiOutlineArrowRightOnRectangle,
  HiOutlineChatBubbleLeftRight,
  HiOutlineClipboardDocument,
  HiOutlineCreditCard,
  HiOutlineDevicePhoneMobile,
  HiOutlineHome,
  HiOutlineKey,
  HiOutlineMagnifyingGlass,
  HiOutlinePaperAirplane,
  HiOutlineQueueList,
  HiOutlineReceiptRefund,
  HiOutlineShieldCheck,
  HiOutlineSquares2X2,
  HiOutlineUserCircle,
  HiOutlineUserGroup,
  HiOutlineWallet,
  HiOutlineXMark,
} from "react-icons/hi2";
import { TbApi } from "react-icons/tb";
import { useEffect } from "react";
import { setBalance } from "store/reducers/balanceReducer";

const mainLinks = [
  // {
  //   url: "/control-panel",
  //   icon: <HiOutlineHome className="text-orange-400" />,
  //   title: "الرئيسية",
  // },
  {
    url: "/control-panel/categories",
    icon: <HiOutlineSquares2X2 className="text-orange-400" />,
    title: "المنتجات",
  },
  // {
  //   url: "/control-panel/search",
  //   icon: <HiOutlineMagnifyingGlass className="text-orange-400" />,
  //   title: "البحث",
  // },
  {
    url: "/control-panel/my-orders",
    icon: <HiOutlineQueueList className="text-orange-300" />,
    title: "طلباتي",
  },
  {
    url: "/control-panel/financial-statement",
    icon: <HiOutlineReceiptRefund className="text-amber-400" />,
    title: "البيان المالي",
  },
  {
    url: "/control-panel/bank-notice",
    icon: <HiOutlineWallet className="text-orange-400" />,
    title: "إضافة رصيد",
  },
  {
    url: "/control-panel/my-payments",
    icon: <HiOutlineCreditCard className="text-orange-300" />,
    title: "دفعاتي",
  },
  // {
  //   url: "/control-panel/tawcik",
  //   icon: <HiOutlineShieldCheck className="text-amber-400" />,
  //   title: "توثيق الحساب",
  // },
  // {
  //   url: "/control-panel/two-factor",
  //   icon: <HiOutlineKey className="text-violet-400" />,
  //   title: "المصادقة الثنائية",
  // },
];

const settingsLinks = [
  {
    url: "/control-panel/our-agents",
    icon: <HiOutlineUserGroup className="text-orange-400" />,
    title: "وكلاؤنا",
  },
  {
    url: "/control-panel/contact-us",
    icon: <HiOutlineChatBubbleLeftRight className="text-orange-500" />,
    title: "تواصل معنا",
  },
  {
    url: "/control-panel/android/download",
    icon: <HiOutlineDevicePhoneMobile className="text-orange-400" />,
    title: "تطبيق الأندرويد",
  },
  {
    url: "/control-panel/telegram-notifications",
    icon: <HiOutlineChatBubbleLeftRight className="text-blue-400" />,
    title: "إشعارات تيليغرام",
  },
  {
    url: "/control-panel/change-password",
    icon: <HiOutlineKey className="text-rose-400" />,
    title: "تغيير كلمة المرور",
  },
  {
    url: "/logout",
    icon: <HiOutlineArrowRightOnRectangle className="text-red-400" />,
    title: "خروج",
  },
  // {
  //   url: "",
  //   icon: <HiOutlineClipboardDocument className="text-red-400" />,
  //   title: "  تطوير Dr Tech",
  // },
];

function SidebarLink({ link, navigate, isActive, mobile, closeSidebar }) {
  return (
    <li>
      <button
        type="button"
        onClick={() => {
          navigate(link.url);
          if (mobile) closeSidebar(false);
        }}
        className={`portal-link flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-right text-nowrap duration-300 group ${
          isActive ? "is-active" : ""
        }`}
      >
        <span className="text-2xl duration-300 group-hover:scale-110">
          {link.icon}
        </span>
        <span>{link.title}</span>
      </button>
    </li>
  );
}

SidebarLink.propTypes = {
  link: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  isActive: PropTypes.bool.isRequired,
  mobile: PropTypes.bool,
  closeSidebar: PropTypes.func.isRequired,
};

function SidebarSection({
  title,
  links,
  navigate,
  currentLocation,
  mobile,
  closeSidebar,
}) {
  return (
    <ul className="mt-4 flex flex-col gap-1 px-3 py-1">
      <h2 className="px-2 text-xs font-bold uppercase tracking-[0.24em] portal-accent">
        {title}
      </h2>
      {links.map((link) => (
        <SidebarLink
          key={link.url}
          link={link}
          navigate={navigate}
          isActive={currentLocation === link.url}
          mobile={mobile}
          closeSidebar={closeSidebar}
        />
      ))}
    </ul>
  );
}

SidebarSection.propTypes = {
  title: PropTypes.string.isRequired,
  links: PropTypes.array.isRequired,
  navigate: PropTypes.func.isRequired,
  currentLocation: PropTypes.string.isRequired,
  mobile: PropTypes.bool,
  closeSidebar: PropTypes.func.isRequired,
};

export default function Sidebar({ isOpen, toggle }) {
  const currentLocation = useCurrentLocation();
  const navigate = useViewTransitionNavigate();
  const { name, roles } = useSelector((state) => state.auth);
  const { isBroker, isApi } = useSelector((state) => state.balance);
  const dispatch = useDispatch();

  const {
    data: myBalance,
    isFetching: myBalanceIsLoading,
    isSuccess: myBalanceIsSuccess,
  } = useMyBalance();

  useEffect(() => {
    if (myBalanceIsSuccess) {
      dispatch(
        setBalance({
          currency: myBalance?.data.currency,
          balance: myBalance?.data.balance,
          isBroker: myBalance?.data.isBroker,
          isApi: myBalance?.data.isApi,
          apiToken: myBalance?.data.apiToken,
          centerName: myBalance?.data.centerName,
          address: myBalance?.data.address,
          city: myBalance?.data.city,
          vrs: myBalance?.data?.vrs || null,
        }),
      );
    }
  }, [dispatch, myBalance, myBalanceIsSuccess]);

  const primaryLinks = [
    ...mainLinks,
    ...(isBroker
      ? [
          {
            title: "إرسال رصيد",
            url: "/control-panel/transfer",
            icon: <HiOutlinePaperAirplane className="text-orange-300" />,
          },
        ]
      : []),
    ...(isApi
      ? [
          {
            title: "ربط API",
            url: "/control-panel/api",
            icon: <TbApi className="text-orange-300" />,
          },
        ]
      : []),
  ];

  const renderSidebarContent = (mobile = false) => (
    <div className="portal-sidebar flex h-full flex-col overflow-y-auto border-l px-3 py-4">
      <div className="portal-accent-bg mb-4 flex items-center justify-between rounded-2xl px-4 py-3 text-white">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-3 text-right"
          onClick={() => {
            navigate("/control-panel/my-profile");
            if (mobile) toggle(false);
          }}
        >
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-white/10 text-3xl">
            <HiOutlineUserCircle />
          </div>
          <div className="min-w-0">
            <h1 className="truncate text-sm font-bold">{name}</h1>
            <p className="truncate text-xs text-white/80">{roles[0]?.name}</p>
          </div>
        </button>

        {mobile ? (
          <button
            type="button"
            className="rounded-xl bg-white/10 p-2 text-xl text-white"
            onClick={() => toggle(false)}
            aria-label="إغلاق القائمة الجانبية"
          >
            <HiOutlineXMark />
          </button>
        ) : null}
      </div>

      <div className="portal-panel rounded-2xl px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <span
            className="text-sm font-semibold"
            style={{ color: "var(--layout-text-muted)" }}
          >
            الرصيد
          </span>
          {myBalance?.data?.vrs ? (
            <HiOutlineShieldCheck
              title="حساب موثق"
              className="text-xl portal-accent"
            />
          ) : null}
        </div>

        <div dir="ltr" className="mt-2 text-2xl font-bold portal-accent">
          {myBalanceIsLoading ? (
            <span className="portal-accent-bg grid h-7 w-7 place-items-center rounded-md">
              <Spinner xs />
            </span>
          ) : null}

          {myBalanceIsSuccess && !myBalanceIsLoading ? (
            <span>
              {Number(myBalance?.data.balance).toLocaleString()}{" "}
              <span className="text-sm font-medium">
                {myBalance?.data.currency}
              </span>
            </span>
          ) : null}
        </div>
      </div>

      <div className="mt-4 flex h-full flex-col justify-between gap-4">
        <SidebarSection
          title="القائمة الرئيسية"
          links={primaryLinks}
          navigate={navigate}
          currentLocation={currentLocation}
          mobile={mobile}
          closeSidebar={toggle}
        />
        <SidebarSection
          title="الإعدادات"
          links={settingsLinks}
          navigate={navigate}
          currentLocation={currentLocation}
          mobile={mobile}
          closeSidebar={toggle}
        />
      </div>
    </div>
  );

  return (
    <>
      <aside className="fixed right-0 top-0 hidden h-full w-72 lg:block">
        {renderSidebarContent()}
      </aside>

      <aside
        className={`fixed right-0 top-0 z-50 h-dvh w-[85vw] max-w-sm transition-transform duration-300 lg:hidden ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        aria-hidden={!isOpen}
      >
        {renderSidebarContent(true)}
      </aside>

      {isOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-40 lg:hidden"
          style={{ background: "var(--layout-overlay)" }}
          onClick={() => toggle(false)}
          aria-label="إغلاق القائمة الجانبية"
        />
      ) : null}
    </>
  );
}

Sidebar.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
};
